import { useLingui } from '@lingui/react/macro';
import { Stack, SxProps, Theme } from '@mui/material';

import { Flags } from '@watershed/constants/flags';
import { PermissionType } from '@watershed/constants/permissions';
import BookIcon from '@watershed/icons/components/Book';
import CircleCheckmarkIcon from '@watershed/icons/components/CircleCheckmark';
import ColumnIcon from '@watershed/icons/components/Column';
import DatabaseIcon from '@watershed/icons/components/Database';
import ListIcon from '@watershed/icons/components/List';
import ListChecksIcon from '@watershed/icons/components/ListChecks';
import NodeIcon from '@watershed/icons/components/Node';
import CleanPowerIcon from '@watershed/icons/components/CleanPower';
import {
  BackTo,
  routeForApiUploadIndex,
  routeForCsrdDataRequirements,
  routeForDatasets,
  routeForEacs,
  routeForEditCompanyTags,
  routeForOrgStructure,
  routeForUploadSchemasIndex,
} from '@watershed/shared-universal/dashboardRoutes';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';
import Well from '@watershed/ui-core/components/Well';
import {
  WellIconLink,
  WellIconLinkProps,
} from '@watershed/ui-core/components/WellIconLink';

import { useFeatureFlag } from '../../utils/FeatureFlag';
import { useUserContext } from '../../utils/UserContext';
import useHasPermission from '../../utils/useHasPermission';

/**
 * This component does not refer to datasources or datasets outside of
 * permission checks.
 *
 * It is links to tools, agnostic of underlying datastructures.
 */
export default function ProjectsListSidebar() {
  const { t } = useLingui();

  const hasCleanPowerV2 = useFeatureFlag(Flags.CleanPowerV2);

  const userContext = useUserContext();
  const { userIsWatershedEmployee, userIsWatershedContractor } = userContext;
  const isCsrdCustomer = useFeatureFlag(
    Flags.PricingFy25CsrdReportBuilderModuleTemp
  );
  const canSeeInternalTools =
    userIsWatershedEmployee || userIsWatershedContractor;
  const canSeeCsrdDataRequirements = canSeeInternalTools && isCsrdCustomer;

  const hasManageOrgStructurePermission = useHasPermission(
    PermissionType.ManageOrgHierarchy
  );
  const hasMeasurementProjectPermission = useHasPermission([
    PermissionType.ManageMeasurement,
  ]);
  const hasManageAllDatasetsPermission = useHasPermission(
    PermissionType.ManageDataset,
    { allowAnyObject: true }
  );

  const sidebarStyles: SxProps<Theme> = {
    padding: 2,
    gap: 2,
    borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
  };

  const toolsItems = [
    {
      IconComponent: NodeIcon,
      href: routeForOrgStructure(),
      title: t({
        message: 'Organizational structure',
        context: 'Measurement projects sidebar link',
      }),
      disabled: !hasManageOrgStructurePermission,
      'data-testid': TestIds.ProjectsListSidebarOrgStructure,
    },
    {
      IconComponent: ColumnIcon,
      href: routeForEditCompanyTags(BackTo.Measurements),
      title: t({
        message: 'Custom columns',
        context: 'Measurement projects sidebar link',
      }),
      disabled: !hasMeasurementProjectPermission,
    },
    {
      IconComponent: ListChecksIcon,
      href: routeForUploadSchemasIndex(),
      title: t({ message: 'Data requirements', context: 'Link copy' }),
    },
    hasCleanPowerV2
      ? {
          IconComponent: CleanPowerIcon,
          href: routeForEacs(),
          title: t({
            message: 'Energy attribute certificates',
            context: 'Measurement projects sidebar link',
          }),
        }
      : null,
  ];

  const apiItems = [
    {
      IconComponent: BookIcon,
      href: 'https://api-docs.watershed.com',
      title: t({ message: 'API docs', context: 'Link copy' }),
    },
    {
      IconComponent: DatabaseIcon,
      href: routeForApiUploadIndex(),
      title: t({ message: 'API uploads', context: 'Link copy' }),
      disabled:
        !hasMeasurementProjectPermission && !hasManageAllDatasetsPermission,
    },
    {
      title: t({
        message: 'Datasets',
        context: 'Measurement projects sidebar link',
      }),
      IconComponent: ListIcon,
      href: routeForDatasets(),
      disabled: !hasMeasurementProjectPermission,
    },
  ];

  const internalToolsItems = [
    canSeeCsrdDataRequirements
      ? {
          IconComponent: CircleCheckmarkIcon,
          href: routeForCsrdDataRequirements(),
          title: t({
            message: 'CSRD data requirements',
            context: 'Measurement projects sidebar link',
          }),
        }
      : null,
  ];

  const sidebarWellGroups = [
    {
      title: t({
        message: 'Tools',
        context: 'Measurement projects sidebar group title',
      }),
      items: toolsItems,
    },
    {
      title: t({
        message: 'API',
        context: 'Measurement projects sidebar group title',
        comment:
          'This section houses documentation and resources about our API',
      }),
      items: apiItems,
    },

    canSeeInternalTools
      ? {
          title: t({
            message: 'Internal tools',
            context: 'Measurement projects sidebar group title',
            comment:
              'This section houses links to internal (Watershed-only) tools',
          }),
          items: internalToolsItems,
        }
      : null,
  ];

  const wellComponentsWithNulls = sidebarWellGroups.map((group) => {
    if (!group) return null;
    const { title, items } = group;
    const filteredItems = items.filter(isNotNullish);
    if (filteredItems.length === 0) return null;

    return <SidebarWellGroup key={title} title={title} items={filteredItems} />;
  });

  return (
    <>
      <Stack
        component="aside"
        sx={sidebarStyles}
        data-testid={TestIds.ProjectsListSidebar}
      >
        {wellComponentsWithNulls.filter(isNotNullish)}
      </Stack>
    </>
  );
}

function SidebarWellGroup({
  title,
  items,
}: {
  title: string;
  items: Array<WellIconLinkProps>;
}) {
  return (
    <Well>
      <Well.Header title={title} titleVariant="h5" titleLevel={2} />
      {items.map((item) => (
        <WellIconLink
          key={item.title}
          IconComponent={item.IconComponent}
          href={item.href}
          onClick={item.onClick}
          title={item.title}
          disabled={item.disabled}
          adornment={item.adornment}
          data-testid={item['data-testid']}
        />
      ))}
    </Well>
  );
}
